import { Controller } from '@hotwired/stimulus'
import {
  computePosition,
  autoUpdate,
  autoPlacement,
  flip,
  shift,
  offset,
  arrow
} from '@floating-ui/dom'
import { wrapViewTransition } from '../helpers/view_transitions'

const OFFSET = 8 // px
const ARROW_OFFSET = -4 // px

// Connects to data-controller="tooltip"
export default class extends Controller {
  static targets = ['trigger', 'tooltip', 'arrow']
  static values = {
    position: { type: String },
    alwaysShow: { type: Boolean, default: false },
    hideTimeout: { type: Number, default: 300 },
    showTimeout: { type: Number, default: 0 }
  }

  set reference (reference) {
    this._reference = reference
  }

  get reference () {
    return this._reference || this.triggerTarget
  }

  connect () {
    this.initTooltip()

    // In case of Turbo Morph event, we need to portal out the tooltip
    // element and declare the event listeners again
    document.addEventListener('turbo:morph', () => {
      this.initTooltip()
    })
  }

  initTooltip () {
    this.tooltipElement = this.tooltipTarget
    this.arrowElement = this.arrowTarget
    this.cleanup = () => {}
    this.scheduledTimeout = null

    // We "portal out" the element to the end of the body/open modal dialog to avoid z-index and clipping issues
    const openModalDialog = document.querySelector('dialog[open]:modal')

    if (openModalDialog) {
      openModalDialog.appendChild(this.tooltipElement)
    } else {
      document.body.appendChild(this.tooltipElement)
    }

    document.addEventListener('turbo:before-render', () => {
      if (this.tooltipElement === null) return

      // We "portal in" the element back to the original parent before render/cache
      // so it can start all over again when the page is restored
      this.element.appendChild(this.tooltipElement)
      this.hide()
    })

    if (!this.hasTriggerTarget) return

    let entered = false

    this.triggerTarget.addEventListener('mouseenter', () => {
      this.show()
    })

    this.tooltipElement.addEventListener('mouseenter', () => {
      entered = true

      if (this.scheduledTimeout) clearTimeout(this.scheduledTimeout)
    })

    this.tooltipElement.addEventListener('mouseleave', () => {
      entered = false

      this.scheduledTimeout = setTimeout(() => {
        this.hide()
      }, this.hideTimeoutValue)
    })

    this.triggerTarget.addEventListener('mouseleave', () => {
      this.scheduledTimeout = setTimeout(() => {
        if (entered) return
        this.hide()
      }, this.hideTimeoutValue)
    })
  }

  show () {
    if (this.scheduledTimeout) clearTimeout(this.scheduledTimeout)

    this.compute()
    setTimeout(() => {
      this.tooltipElement.removeAttribute('hidden')
    }, this.showTimeoutValue)
  }

  hide () {
    window.requestAnimationFrame(() => {
      wrapViewTransition(() => {
        this.tooltipElement.setAttribute('hidden', '')
        this.cleanup()
      })
    })
  }

  compute () {
    const hasAutoPlacement = this.positionValue === 'auto'
    const defaultMiddlewares = [
      shift({ padding: OFFSET }),
      offset(OFFSET),
      arrow({ element: this.arrowElement })
    ]

    this.cleanup = autoUpdate(this.triggerTarget, this.tooltipElement, () => {
      computePosition(
        this.reference,
        this.tooltipElement,
        {
          placement: this.positionValue,
          middleware: hasAutoPlacement
            ? [autoPlacement(), ...defaultMiddlewares]
            : [flip(), ...defaultMiddlewares]
        }
      ).then((data) => this.applyStyles(data))
    })
  }

  applyStyles ({ x, y, placement, middlewareData }) {
    Object.assign(this.tooltipElement.style, {
      top: `${y}px`,
      left: `${x}px`
    })

    const { x: arrowX, y: arrowY } = middlewareData.arrow
    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right'
    }[placement.split('-')[0]]

    Object.assign(this.arrowElement.style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: `${ARROW_OFFSET}px`
    })
  }
}
